/**
 * @fileoverview Input/output schemas for the auto-gather agent.
 *
 * This agent selects a thematically coherent batch of findings and weaknesses
 * for refinement from available critique data.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import {
  IssueSeverityEnum,
  IssueConfidenceEnum,
  IssueDomainEnum,
} from '@/ai/agents/issue-classification';

// =============================================================================
// SHARED SCHEMAS
// =============================================================================

/**
 * Schema for a finding item passed to the auto-gather agent.
 * Uses the unified issue classification schema.
 */
const FindingItemSchema = z.object({
  id: z.string().describe('Unique identifier for this finding'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  category: z.string().describe('The category of this finding from unified taxonomy'),
  description: z.string().describe('Clear explanation of the issue'),
  excerpt: z.string().describe('Direct quote from the text where this issue occurs'),
  suggestion: z.string().optional().describe('Suggestion for how to address this issue'),
  sourceAgentName: z.string().describe('Display name of the agent that created this finding'),
});

/**
 * Schema for a weakness item passed to the auto-gather agent.
 * Uses the unified issue classification schema.
 */
const WeaknessItemSchema = z.object({
  id: z.string().describe('Unique identifier for this weakness'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  domain: IssueDomainEnum.describe('The domain of this weakness'),
  description: z.string().describe('Description of the high-level area needing improvement'),
  sourceAgentName: z.string().describe('Display name of the agent that identified this weakness'),
});

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema.extend({
  // All available critique data
  findings: z.array(FindingItemSchema).describe(
    'All available findings from critique agents'
  ),
  weaknesses: z.array(WeaknessItemSchema).describe(
    'All available weaknesses from critique agents'
  ),

  // User's pre-selected items (to respect and augment)
  preSelectedFindingIds: z.array(z.string()).describe(
    'IDs of findings already selected by the user'
  ),
  preSelectedWeaknessIds: z.array(z.string()).describe(
    'IDs of weaknesses already selected by the user'
  ),

  // Configuration
  maxItems: z.number().min(1).max(20).describe(
    'Maximum number of items to select (findings + weaknesses combined)'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for the auto-gather agent.
 */
export const OutputSchema = z.object({
  selectedFindingIds: z.array(z.string()).describe(
    'IDs of findings selected for the refine session'
  ),
  selectedWeaknessIds: z.array(z.string()).describe(
    'IDs of weaknesses selected for the refine session'
  ),

  // Rationale for the grouping
  groupingTheme: z.string().describe(
    'Short label for the theme of the selected items (e.g., "Dialogue & Character Voice")'
  ),
  rationale: z.string().describe(
    '2-3 sentences explaining why these items were grouped together'
  ),
  suggestedApproach: z.string().describe(
    'Brief guidance for how to approach the refinement of these items'
  ),

  // Metadata
  remainingIssuesCount: z.number().describe(
    'How many items were not selected (for potential follow-up batches)'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
